/**
 * Escenografía de cada tipo de sala: qué muebles lleva, dónde van y en qué
 * puntos puede pararse o sentarse un avatar.
 *
 * Las x van en unidades de mundo desde el borde izquierdo de la sala. Un punto
 * (`spot`) dice la pose, hacia dónde mira y qué mueble enciende mientras se usa.
 */
import { hashId } from './pixelKit';

const BASE_W = 340;
const SEAT_SHIFT = 12;

/** x del mueble para que quien se siente en `spotX` quede sobre el asiento. */
function seatFor(spotX, facing) {
    return spotX - SEAT_SHIFT * facing;
}

function officeScene(room, X, seed) {
    const deskSpot = X(76);
    const items = [
        { type: 'window', x: X(46), seed },
        { type: 'whiteboard', x: X(252), seed },
        { type: 'ceilLamp', x: X(150), color: room.look.trim },
        { type: 'rug', x: X(128), w: 30, color: room.look.trim },
        { type: 'chair', x: seatFor(deskSpot, 1) },
        { type: 'desk', x: deskSpot + 62, use: 'desk', alert: room.alert, seed, tone: ['#3ad0c0', '#ffb84a', '#ff7ac0', '#8fd0ff'][seed % 4] },
        { type: 'pouf', x: X(296), color: ['#d4893a', '#7a4ea0', '#4a7c59'][seed % 3] },
    ];
    if (room.decor === 0) items.push({ type: 'poster', x: X(142), seed }, { type: 'plant', x: X(22), seed });
    else if (room.decor === 1) items.push({ type: 'clock', x: X(142) }, { type: 'cabinet', x: X(28) });
    else items.push({ type: 'shelf', x: X(143), seed }, { type: 'bin', x: X(20) }, { type: 'plant', x: X(326), seed: seed + 1 });
    return {
        items,
        work: [
            { id: 'desk', x: deskSpot, poses: ['type'], facing: 1 },
            { id: 'board', x: X(222), poses: ['point', 'look'], facing: 1 },
            { id: 'pouf', x: X(296) - SEAT_SHIFT, poses: ['sitRead', 'sitPhone'], facing: -1 },
        ],
        spots: [],
    };
}

function emptyScene(room, X, seed) {
    return {
        items: [
            { type: 'window', x: X(270), seed },
            { type: 'bulb', x: X(160) },
            { type: 'boxes', x: X(74) },
            { type: 'ladder', x: X(186) },
            { type: 'boxes', x: X(304) },
        ],
        work: [],
        spots: [],
    };
}

function hallScene(room, X, seed) {
    const items = [];
    const modules = Math.max(1, Math.round(room.w / BASE_W));
    for (let m = 0; m < modules; m += 1) {
        const base = m * BASE_W;
        items.push(
            { type: 'window', x: base + 82, seed: seed + m },
            { type: 'window', x: base + 252, seed: seed + m + 9 },
            { type: 'ceilLamp', x: base + 168, color: room.look.trim },
            { type: 'bench', x: base + 166 },
            { type: m % 2 ? 'plant' : 'bigPlant', x: base + 26, seed: seed + m },
            { type: 'extinguisher', x: base + 318 },
        );
        if (m === 0) items.push({ type: 'printer', x: base + 252, seed });
        else items.push({ type: 'waterCooler', x: base + 250, seed });
    }
    items.push({ type: 'exitSign', x: room.w - 70 });
    return { items, work: [], spots: [] };
}

function lobbyScene(room, X, seed) {
    const wide = room.w > 420;
    const items = [
        { type: 'entrance', x: -13, use: 'door' },
        { type: 'noticeBoard', x: X(84), seed },
        { type: 'sofa', x: X(84), color: '#4a6a8a', cushion: '#e0b23c' },
        { type: 'logoSign', x: X(208) },
        { type: 'reception', x: X(208), use: 'desk' },
        { type: 'clock', x: X(290) },
        { type: 'waterCooler', x: X(266), use: 'cooler', seed },
        { type: 'printer', x: X(312), seed },
        { type: 'ceilLamp', x: X(100), color: '#d4a017' },
        { type: 'ceilLamp', x: X(262), color: '#d4a017' },
    ];
    if (wide) items.push({ type: 'coffeeTable', x: X(142) }, { type: 'bigPlant', x: X(170), seed });
    return {
        items,
        work: [],
        spots: [
            { id: 'sofaL', x: X(84) - 27 + SEAT_SHIFT, poses: ['sitPhone', 'sitRead', 'sit'], facing: 1 },
            { id: 'sofaR', x: X(84) + 27 - SEAT_SHIFT, poses: ['sit', 'sitPhone', 'nap'], facing: -1 },
            { id: 'stand', x: X(148), poses: ['phone', 'idle', 'stretch'], facing: 1 },
            { id: 'desk', x: X(208) - 14, poses: ['use'], facing: 1 },
            { id: 'cooler', x: X(266) - 24, poses: ['drink'], facing: 1 },
        ],
    };
}

function loungeScene(room, X, seed) {
    return {
        items: [
            { type: 'shelf', x: X(34), seed },
            { type: 'fishtank', x: X(32), seed },
            { type: 'window', x: X(122), seed },
            { type: 'rug', x: X(122), w: 30, color: '#b8475f' },
            { type: 'sofa', x: X(122), color: '#8a4a5a', cushion: '#8fd0ff' },
            { type: 'tv', x: X(218), use: '*', seed },
            { type: 'foosball', x: X(218), use: 'foos' },
            { type: 'arcade', x: X(312), use: 'arcade' },
            { type: 'discoBall', x: X(170) },
            { type: 'ceilLamp', x: X(84), color: '#7a4ea0', party: true },
            { type: 'ceilLamp', x: X(268), color: '#7a4ea0', party: true },
        ],
        work: [],
        spots: [
            { id: 'tank', x: X(32) + 34, poses: ['look', 'phone'], facing: -1 },
            { id: 'sofaL', x: X(122) - 27 + SEAT_SHIFT, poses: ['sit', 'sitRead', 'nap', 'sitPhone'], facing: 1 },
            { id: 'sofaR', x: X(122) + 27 - SEAT_SHIFT, poses: ['sitPhone', 'sit', 'sitRead'], facing: -1 },
            { id: 'foos', x: X(218) - 50, poses: ['play'], facing: 1 },
            { id: 'foos2', x: X(218) + 50, poses: ['play'], facing: -1, use: 'foos' },
            { id: 'arcade', x: X(312) - 22, poses: ['use'], facing: 1 },
        ],
    };
}

function cafeScene(room, X, seed) {
    return {
        items: [
            { type: 'fridge', x: X(26), use: 'fridge', seed },
            { type: 'menuBoard', x: X(104) },
            { type: 'counter', x: X(104) },
            { type: 'coffeeMachine', x: X(84), use: 'coffee', seed },
            { type: 'microwave', x: X(126), use: 'micro' },
            { type: 'neon', x: X(186), seed, label: 'CAFÉ' },
            { type: 'vending', x: X(186), use: 'vending', seed },
            { type: 'window', x: X(270), seed },
            { type: 'stool', x: X(240) },
            { type: 'stool', x: X(300) },
            { type: 'cafeTable', x: X(270) },
            { type: 'pendant', x: X(104), color: '#e0503c' },
            { type: 'pendant', x: X(270), color: '#e0503c' },
            { type: 'bin', x: X(328) },
        ],
        work: [],
        spots: [
            { id: 'fridge', x: X(26) + 26, poses: ['use'], facing: -1 },
            { id: 'coffee', x: X(84) + 22, poses: ['use'], facing: -1, gives: 'mug' },
            { id: 'micro', x: X(126) + 24, poses: ['look', 'phone'], facing: -1 },
            { id: 'vending', x: X(186) + 26, poses: ['use'], facing: -1 },
            { id: 'stoolL', x: X(240) + SEAT_SHIFT, poses: ['sitEat', 'sitDrink', 'sitTalk'], facing: 1 },
            { id: 'stoolR', x: X(300) - SEAT_SHIFT, poses: ['sitDrink', 'sitTalk', 'sitEat'], facing: -1 },
        ],
    };
}

function bathScene(room, X, seed) {
    return {
        items: [
            { type: 'stall', x: X(40) },
            { type: 'stallDoor', x: X(40), use: 's1' },
            { type: 'stall', x: X(110) },
            { type: 'stallDoor', x: X(110), use: 's2' },
            { type: 'ceilLamp', x: X(170), color: '#6aa8a0' },
            { type: 'sink', x: X(190), use: 'k1', seed },
            { type: 'sink', x: X(250), use: 'k2', seed: seed + 5 },
            { type: 'dryer', x: X(306), use: 'dry' },
            { type: 'wetSign', x: X(328) },
        ],
        work: [],
        spots: [
            { id: 's1', x: X(40), poses: ['idle'], facing: 1, hidden: true, takes: true },
            { id: 's2', x: X(110), poses: ['idle'], facing: 1, hidden: true, takes: true },
            { id: 'k1', x: X(190) - 22, poses: ['wash'], facing: 1, takes: true },
            { id: 'k2', x: X(250) - 22, poses: ['wash'], facing: 1, takes: true },
            { id: 'dry', x: X(306) - 20, poses: ['use'], facing: 1, takes: true },
        ],
    };
}

const BUILDERS = {
    office: officeScene,
    empty: emptyScene,
    hall: hallScene,
    lobby: lobbyScene,
    lounge: loungeScene,
    cafe: cafeScene,
    bath: bathScene,
};

/** Probabilidad de que un avatar libre elija cada sala común. */
export const COMMON_WEIGHT = { lobby: 22, lounge: 33, cafe: 33, bath: 12 };

/** Arma la escena de una sala ya colocada (necesita `room.w` y `room.look`). */
export function sceneFor(room) {
    const scale = room.w / BASE_W;
    const X = (value) => Math.round(value * scale);
    const build = BUILDERS[room.kind] || emptyScene;
    const scene = build(room, X, hashId(room.id));
    scene.items.push({ type: 'emergency', x: room.w - 18, seed: hashId(room.id) % 5 });
    return scene;
}
